import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  ContentPaste as PasteIcon,
  Download as DownloadIcon,
  UploadFile as UploadIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import crmContactoService from '../../../services/crmContactoService';
import type { CrmContactoImportFila, CrmContactoImportPreviewFila } from '../../../types/crm';
import { getApiErrorMessage } from '../../../utils/apiError';
import {
  downloadCrmContactosPlantilla,
  parseCrmContactosClipboard,
  parseCrmContactosExcel,
} from '../../../utils/crmContactosImportParse';

interface CrmContactosImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}

type Step = 'origen' | 'preview' | 'done';

const CrmContactosImportDialog: React.FC<CrmContactosImportDialogProps> = ({
  open,
  onClose,
  onImported,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const fileRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>('origen');
  const [pasteText, setPasteText] = useState('');
  const [preview, setPreview] = useState<CrmContactoImportPreviewFila[]>([]);
  const [acciones, setAcciones] = useState<Record<number, 'Actualizar' | 'Omitir'>>({});
  const [loading, setLoading] = useState(false);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [resultado, setResultado] = useState<{
    creados: number;
    actualizados: number;
    omitidos: number;
    errores: { fila: number; mensaje: string }[];
  } | null>(null);

  const reset = () => {
    setStep('origen');
    setPasteText('');
    setPreview([]);
    setAcciones({});
    setParseErrors([]);
    setResultado(null);
    setLoading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const runPreview = async (filas: CrmContactoImportFila[], localErrors: string[]) => {
    if (filas.length === 0) {
      setParseErrors(localErrors.length ? localErrors : ['No hay filas para importar.']);
      return;
    }
    setLoading(true);
    try {
      const res = await crmContactoService.importarPreview(filas);
      const rows = res.filas ?? [];
      setPreview(rows);
      const initial: Record<number, 'Actualizar' | 'Omitir'> = {};
      rows.forEach((f) => {
        if (f.estado === 'Duplicado') initial[f.fila] = 'Omitir';
      });
      setAcciones(initial);
      setParseErrors(localErrors);
      setStep('preview');
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'No se pudo validar la importación'), {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFile = async (file: File | null) => {
    if (!file) return;
    try {
      setLoading(true);
      const parsed = await parseCrmContactosExcel(file);
      const localErrors = parsed.errores.map((e) =>
        e.fila ? `Fila ${e.fila}: ${e.mensaje}` : e.mensaje,
      );
      await runPreview(parsed.filas, localErrors);
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'No se pudo leer el archivo'), { variant: 'error' });
      setLoading(false);
    }
  };

  const handlePasteArea = async () => {
    const parsed = parseCrmContactosClipboard(pasteText);
    const localErrors = parsed.errores.map((e) =>
      e.fila ? `Fila ${e.fila}: ${e.mensaje}` : e.mensaje,
    );
    await runPreview(parsed.filas, localErrors);
  };

  const handleClipboardButton = async () => {
    try {
      if (navigator.clipboard?.readText) {
        const text = await navigator.clipboard.readText();
        setPasteText(text);
        const parsed = parseCrmContactosClipboard(text);
        const localErrors = parsed.errores.map((e) =>
          e.fila ? `Fila ${e.fila}: ${e.mensaje}` : e.mensaje,
        );
        await runPreview(parsed.filas, localErrors);
        return;
      }
    } catch {
      /* fallback: usuario pega manualmente */
    }
    enqueueSnackbar('Pegá el contenido en el área de texto y pulsá “Procesar pegado”.', {
      variant: 'info',
    });
  };

  const applyAccionMasiva = (accion: 'Actualizar' | 'Omitir') => {
    setAcciones((prev) => {
      const next = { ...prev };
      preview.forEach((f) => {
        if (f.estado === 'Duplicado') next[f.fila] = accion;
      });
      return next;
    });
  };

  const handleConfirm = async () => {
    const filas: CrmContactoImportFila[] = preview
      .filter((f) => f.estado !== 'Error')
      .map((f) => {
        const base: CrmContactoImportFila = {
          fila: f.fila,
          nombre: f.nombre,
          empresa: f.empresa,
          cargo: f.cargo,
          telefono: f.telefono,
          email: f.email,
          origen: f.origen,
          tags: f.tags,
          web: f.web,
          domicilio: f.domicilio,
          ciudad: f.ciudad,
          provincia: f.provincia,
          pais: f.pais,
          atributo1: f.atributo1,
          atributo2: f.atributo2,
          atributo3: f.atributo3,
        };
        if (f.estado === 'Duplicado') {
          return {
            ...base,
            accion: acciones[f.fila] ?? 'Omitir',
            contactoId: f.contactoId,
          };
        }
        return {
          ...base,
          accion: 'Crear',
        };
      });

    if (filas.length === 0) {
      enqueueSnackbar('No hay filas válidas para importar', { variant: 'warning' });
      return;
    }

    setLoading(true);
    try {
      const res = await crmContactoService.importar(filas);
      setResultado(res);
      setStep('done');
      enqueueSnackbar(
        `Importación: ${res.creados} creados, ${res.actualizados} actualizados, ${res.omitidos} omitidos`,
        { variant: 'success' },
      );
      onImported();
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'Error al importar contactos'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const estadoChip = (estado: string) => {
    if (estado === 'Duplicado') return <Chip size="small" label="Duplicado" color="warning" />;
    if (estado === 'Error') return <Chip size="small" label="Error" color="error" />;
    return <Chip size="small" label="Nuevo" color="success" />;
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ fontWeight: 600 }}>Importar contactos</DialogTitle>
      <DialogContent dividers>
        {step === 'origen' && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Subí un Excel/CSV o pegá filas copiadas desde una planilla. Columnas: Nombre (obligatoria),
              Empresa, Cargo, Teléfono, Email, Origen, Tags, Web, Domicilio, Ciudad, Provincia, País,
              Atributo1–3. La plantilla incluye una hoja Instrucciones con tipos y largos.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={downloadCrmContactosPlantilla}
                sx={{ textTransform: 'none' }}
              >
                Descargar plantilla
              </Button>
              <Button
                variant="contained"
                startIcon={<UploadIcon />}
                onClick={() => fileRef.current?.click()}
                disabled={loading}
                sx={{ textTransform: 'none' }}
              >
                Subir archivo
              </Button>
              <Button
                variant="outlined"
                startIcon={<PasteIcon />}
                onClick={() => void handleClipboardButton()}
                disabled={loading}
                sx={{ textTransform: 'none' }}
              >
                Pegar desde portapapeles
              </Button>
              <input
                ref={fileRef}
                type="file"
                hidden
                accept=".xlsx,.xls,.csv"
                onChange={(e) => void handleFile(e.target.files?.[0] ?? null)}
              />
            </Stack>
            <TextField
              label="Pegar datos (separados por tabulación)"
              multiline
              minRows={6}
              fullWidth
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              placeholder="Nombre&#9;Empresa&#9;Cargo&#9;Teléfono&#9;Email&#9;Origen&#9;Tags&#9;Web&#9;Domicilio&#9;Ciudad&#9;Provincia&#9;Pais&#9;Atributo1&#9;Atributo2&#9;Atributo3"
            />
            <Box>
              <Button
                variant="contained"
                disabled={loading || !pasteText.trim()}
                onClick={() => void handlePasteArea()}
                sx={{ textTransform: 'none' }}
              >
                Procesar pegado
              </Button>
            </Box>
          </Stack>
        )}

        {step === 'preview' && (
          <Stack spacing={2}>
            {parseErrors.length > 0 && (
              <Alert severity="warning">
                {parseErrors.map((e) => (
                  <div key={e}>{e}</div>
                ))}
              </Alert>
            )}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                Duplicados:
              </Typography>
              <Button size="small" onClick={() => applyAccionMasiva('Omitir')} sx={{ textTransform: 'none' }}>
                Omitir todos
              </Button>
              <Button
                size="small"
                onClick={() => applyAccionMasiva('Actualizar')}
                sx={{ textTransform: 'none' }}
              >
                Actualizar todos
              </Button>
            </Stack>
            <TableContainer sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Fila</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Empresa</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Teléfono</TableCell>
                    <TableCell>Acción / detalle</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.map((f) => (
                    <TableRow key={f.fila}>
                      <TableCell>{f.fila}</TableCell>
                      <TableCell>{estadoChip(f.estado)}</TableCell>
                      <TableCell>{f.nombre}</TableCell>
                      <TableCell>{f.empresa ?? '—'}</TableCell>
                      <TableCell>{f.email ?? '—'}</TableCell>
                      <TableCell>{f.telefono ?? '—'}</TableCell>
                      <TableCell>
                        {f.estado === 'Duplicado' ? (
                          <Stack spacing={0.5}>
                            <Typography variant="caption" color="text.secondary">
                              {f.mensaje}
                            </Typography>
                            <TextField
                              select
                              size="small"
                              value={acciones[f.fila] ?? 'Omitir'}
                              onChange={(e) =>
                                setAcciones((prev) => ({
                                  ...prev,
                                  [f.fila]: e.target.value as 'Actualizar' | 'Omitir',
                                }))
                              }
                              sx={{ minWidth: 140 }}
                            >
                              <MenuItem value="Omitir">Omitir</MenuItem>
                              <MenuItem value="Actualizar">Actualizar</MenuItem>
                            </TextField>
                          </Stack>
                        ) : f.estado === 'Error' ? (
                          <Typography variant="caption" color="error">
                            {f.mensaje}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            {f.advertencia ?? 'Se creará'}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        )}

        {step === 'done' && resultado && (
          <Stack spacing={1.5}>
            <Alert severity="success">
              Importación finalizada: {resultado.creados} creados, {resultado.actualizados}{' '}
              actualizados, {resultado.omitidos} omitidos.
            </Alert>
            {resultado.errores.length > 0 && (
              <Alert severity="warning">
                {resultado.errores.map((e) => (
                  <div key={`${e.fila}-${e.mensaje}`}>
                    Fila {e.fila}: {e.mensaje}
                  </div>
                ))}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {step === 'preview' && (
          <Button
            onClick={() => {
              setStep('origen');
              setPreview([]);
            }}
            disabled={loading}
            sx={{ textTransform: 'none' }}
          >
            Volver
          </Button>
        )}
        <Button onClick={handleClose} sx={{ textTransform: 'none' }}>
          {step === 'done' ? 'Cerrar' : 'Cancelar'}
        </Button>
        {step === 'preview' && (
          <Button
            variant="contained"
            disabled={loading}
            onClick={() => void handleConfirm()}
            sx={{ textTransform: 'none' }}
          >
            Confirmar importación
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CrmContactosImportDialog;
